import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Inquiry {
  id: string;
  name: string;
  email: string;
  service: string;
  budget: string;
  message: string;
  created_at: string;
}

export default function InquiriesManager() {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('customer_inquiries')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setInquiries(data);
    }
    setIsLoading(false);
  };

  const deleteInquiry = async (id: string) => {
    if (!confirm('Are you sure you want to delete this inquiry?')) return;

    const { error } = await supabase
      .from('customer_inquiries')
      .delete()
      .eq('id', id);

    if (!error) {
      setInquiries(inquiries.filter(inq => inq.id !== id));
      setSelectedInquiry(null);
    }
  };

  const filteredInquiries = inquiries.filter(inq =>
    inq.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inq.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inq.service.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line text-3xl text-cyan-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Customer Inquiries</h2>
          <p className="text-gray-600 text-sm mt-1">
            {inquiries.length} total {inquiries.length === 1 ? 'inquiry' : 'inquiries'}
          </p>
        </div>
        <button
          onClick={fetchInquiries}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2 text-sm font-semibold whitespace-nowrap cursor-pointer"
        >
          <i className="ri-refresh-line"></i>
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, or service..."
          className="w-full px-4 py-3 pl-12 bg-white border border-gray-200 rounded-xl focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all text-sm"
        />
        <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
          <i className="ri-search-line text-gray-400 text-lg"></i>
        </div>
      </div>

      {/* Inquiries List */}
      {filteredInquiries.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-inbox-line text-gray-400 text-3xl"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No inquiries found</h3>
          <p className="text-gray-600 text-sm">
            {searchTerm ? 'Try adjusting your search terms' : 'Customer inquiries will appear here'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredInquiries.map((inquiry) => (
            <div
              key={inquiry.id}
              className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-cyan-300 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => setSelectedInquiry(inquiry)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{inquiry.name}</h3>
                    <span className="px-3 py-1 bg-cyan-50 text-cyan-700 text-xs font-semibold rounded-full whitespace-nowrap">
                      {inquiry.service.replace(/-/g, ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-mail-line"></i>
                      {inquiry.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-money-dollar-circle-line"></i>
                      {inquiry.budget}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {formatDate(inquiry.created_at)}
                    </span>
                  </div>
                  <p className="text-gray-700 line-clamp-2">{inquiry.message}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteInquiry(inquiry.id);
                  }}
                  className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-all flex-shrink-0 cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-lg"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedInquiry && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50"
          onClick={() => setSelectedInquiry(null)}
        >
          <div
            className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedInquiry.name}</h2>
                <p className="text-sm text-gray-600">{formatDate(selectedInquiry.created_at)}</p>
              </div>
              <button
                onClick={() => setSelectedInquiry(null)}
                className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-all cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1">Email</label>
                <a
                  href={`mailto:${selectedInquiry.email}`}
                  className="text-cyan-600 hover:text-cyan-700 flex items-center gap-2 cursor-pointer"
                >
                  <i className="ri-mail-line"></i>
                  {selectedInquiry.email}
                </a>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1">Service Type</label>
                <p className="text-gray-900">{selectedInquiry.service.replace(/-/g, ' ')}</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1">Budget Range</label>
                <p className="text-gray-900">{selectedInquiry.budget}</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1">Message</label>
                <p className="text-gray-900 whitespace-pre-wrap">{selectedInquiry.message}</p>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <a
                href={`mailto:${selectedInquiry.email}?subject=Re: Your Website Inquiry`}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-send-line"></i>
                Reply via Email
              </a>
              <button
                onClick={() => deleteInquiry(selectedInquiry.id)}
                className="px-6 py-3 bg-red-50 text-red-600 font-semibold rounded-xl hover:bg-red-100 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-delete-bin-line"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
